import { memo, useMemo } from 'react'
import { StagedActionCell } from '@renderer/chat/integrations/staged-action'
import {
  getPreferenceCardTitle,
  getPreferenceWriteError,
  isPreferenceReviewHandoff,
  isSkillPreferenceTool,
  preferenceWriteFailed,
  readPreferenceContent,
  readStringField,
} from '@renderer/chat/integrations/skills/skill-preference-tool'
import { useDefaultSkills, useSkills } from '@renderer/chat/hooks/useSkills'
import type { ToolCall } from '@renderer/chat/types'

interface SkillPreferenceActionsSectionProps {
  toolCalls: Array<ToolCall>
  groupToolCalls?: Array<ToolCall>
}

export const SkillPreferenceActionsSection = memo(function SkillPreferenceActionsSection({
  toolCalls,
  groupToolCalls,
}: SkillPreferenceActionsSectionProps) {
  const { data: userSkills } = useSkills()
  const { data: defaultSkills } = useDefaultSkills()

  const skillNamesById = useMemo(() => {
    const names = new Map<string, string>()
    for (const skill of [...(userSkills ?? []), ...(defaultSkills ?? [])]) {
      names.set(skill.id, skill.name)
      if (skill.forkedFromId) names.set(skill.forkedFromId, skill.name)
    }
    return names
  }, [defaultSkills, userSkills])

  const preferenceWrites = useMemo(() => {
    const byId = new Map<string, ToolCall>()
    for (const toolCall of [...toolCalls, ...(groupToolCalls ?? [])]) {
      if (
        isSkillPreferenceTool(toolCall.toolName) &&
        toolCall.status !== 'cancelled' &&
        toolCall.status !== 'ready' &&
        toolCall.status !== 'executing' &&
        !isPreferenceReviewHandoff(toolCall)
      ) {
        byId.set(toolCall.id, toolCall)
      }
    }
    return Array.from(byId.values())
  }, [groupToolCalls, toolCalls])

  if (preferenceWrites.length === 0) return null

  return (
    <div className="mb-1 mt-2 flex flex-col gap-2">
      {preferenceWrites.map((toolCall) => {
        const skillId = readStringField(toolCall.toolInput, 'id')
        const skillName = skillId ? (skillNamesById.get(skillId) ?? null) : null
        const failed = preferenceWriteFailed(toolCall)
        const title = getPreferenceCardTitle(toolCall.toolName, skillName, failed)
        const content = readPreferenceContent(toolCall)

        return (
          <StagedActionCell
            key={toolCall.id}
            stagedAction={{
              id: toolCall.id,
              actionType: 'skillPreference',
              title,
              description: '',
              toolSet: 'customization',
              toolName: toolCall.toolName,
              view: {
                title,
                icon: 'check',
                content: content ?? '',
                tags: [],
                buttons: [],
                supportsUndo: false,
              },
            }}
            status={failed ? 'error' : 'pending'}
            errorMessage={failed ? getPreferenceWriteError(toolCall) : undefined}
            fullContent
          />
        )
      })}
    </div>
  )
})
